import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { guestService, hotelService } from "@/services/ServiceRegistry";
import {
  CreateGuestDto,
  Guest,
  GuestFilters,
  UpdateGuestDto,
} from "@/types/guest";
import { reservationKeys } from "@/hooks/useReservations";

export const guestKeys = {
  all: ["guests"] as const,
  lists: () => [...guestKeys.all, "list"] as const,
  list: (filters?: GuestFilters, hotelId?: string) =>
    [...guestKeys.lists(), filters, hotelId] as const,
  details: () => [...guestKeys.all, "detail"] as const,
  detail: (id: string) => [...guestKeys.details(), id] as const,
  nationalities: (hotelId?: string) =>
    hotelId
      ? ([...guestKeys.all, "nationalities", hotelId] as const)
      : ([...guestKeys.all, "nationalities"] as const),
};

export const useGuests = (hotelId?: string, filters?: GuestFilters) => {
  return useQuery({
    queryKey: guestKeys.list(filters, hotelId),
    queryFn: () => hotelService.getGuestsWithLastBooking(hotelId!, filters),
    staleTime: 5 * 60 * 1000,
    enabled: !!hotelId,
  });
};

export const useGuest = (id: string) => {
  return useQuery({
    queryKey: guestKeys.detail(id),
    queryFn: () => guestService.getGuestById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useGuestNationalities = (hotelId?: string) => {
  return useQuery({
    queryKey: guestKeys.nationalities(hotelId),
    queryFn: () => hotelService.getNationalities(hotelId),
    enabled: !!hotelId,
  });
};

export const useCreateGuest = (hotelId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (guestData: CreateGuestDto) =>
      guestService.createGuest({ hotelId, ...guestData }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guestKeys.lists() });
      queryClient.invalidateQueries({ queryKey: guestKeys.nationalities() });
    },
  });
};

export const useUpdateGuest = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { guest: Guest },
    Error,
    { id: string; data: UpdateGuestDto }
  >({
    mutationFn: ({ id, data }: { id: string; data: UpdateGuestDto }) =>
      guestService.updateGuest(id, data),
    onSuccess: (response: { guest: Guest }) => {
      queryClient.setQueryData(
        guestKeys.detail(response.guest.id),
        response.guest,
      );
      queryClient.invalidateQueries({ queryKey: guestKeys.lists() });
      queryClient.invalidateQueries({ queryKey: guestKeys.nationalities() });
      queryClient.invalidateQueries({ queryKey: reservationKeys.all });
    },
  });
};
